import React, { useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import BlockCardV3 from './BlockCardV3'
import HeadlineBlockCard from './HeadlineBlockCard'
import { textAligner } from '../../helpers/stylesAligner'
import ContentfulButtonV3 from '../Utilities/Buttons/V3/ContentfulButtonV3'
import carouselize from '../Utilities/Carouselize/Carouselize'
import { Arrow } from '../icons'

function BlockCardGrid({ data }) {
  const {
    blockCards,
    grid,
    isBlog,
    headline,
    subtitle,
    showcaseCircles,
    theme,
    alignText,
    ctaButton,
    displayInMobile,
    showContentAsCarousel,
    carouselArrowsColor,
    quote,
    backgroundImageGrid,
    activeLeadershipDesign,
    __typename
  } = data

  const swipeContainer = useRef(null)
  const itemContainer = useRef(null)
  const prevButton = useRef(null)
  const nextButton = useRef(null)

  const gridClass = columns => {
    if (columns === 'multiples of 3') {
      return 'grid-three-columns'
    }

    if (columns === 'multiples of 2') {
      return 'grid-two-columns'
    }

    if (columns === 'multiples of 2 (left is wider)') {
      return 'grid-two-columns-left-wider'
    }

    if (quote) {
      return 'grid-default quote-grid'
    }

    if (backgroundImageGrid) {
      return 'grid-bg-image'
    }

    return 'grid-default'
  }

  const setArrowColor = colorName => {
    if (colorName === 'Navy') {
      return '#2a2a3f'
    }
    if (colorName === 'White') {
      return '#fff'
    }

    return '#0d5bff'
  }

  const arrows = (
    <>
      <button type="button" className="prev-button" ref={prevButton}>
        <Arrow
          className="carousel-button__icon--prev"
          width={31}
          height={15.5}
          fill={setArrowColor(carouselArrowsColor)}
          title="previous button"
        />
      </button>
      <button type="button" className="next-button" ref={nextButton}>
        <Arrow
          className="carousel-button__icon"
          width={31}
          height={15.5}
          fill={setArrowColor(carouselArrowsColor)}
          title="next button"
        />
      </button>
    </>
  )

  let carouselLayout
  // if carousel is set in mobile and dekstop and is NOT a quote section becasuse this section has very specific styles
  if (!displayInMobile && showContentAsCarousel && !quote) {
    carouselLayout = 'mobile-desktop-carousel'
  }

  // if carousel is set only in mobile
  if (!displayInMobile && !showContentAsCarousel) {
    carouselLayout = 'mobile-carousel'
  }

  // if carousel is set only in desktop
  if (displayInMobile && showContentAsCarousel) {
    carouselLayout = 'desktop-carousel'
  }

  const carousel =
    (blockCards && blockCards.length > 1 && showContentAsCarousel) ||
    (blockCards && blockCards.length > 1 && !displayInMobile && displayInMobile !== null) ? (
      <div className={`swipe-container ${carouselLayout}`} ref={swipeContainer}>
        <div className="item-container" ref={itemContainer}>
          {blockCards !== null &&
            blockCards.map(card => {
              return (
                <div className="box" key={JSON.stringify(card)}>
                  <BlockCardV3
                    data={card}
                    grid
                    columns={gridClass(grid)}
                    blog={isBlog}
                    showcaseCircles={showcaseCircles}
                    gridTheme={theme}
                    imageGrid={backgroundImageGrid}
                    quote={quote}
                    useDivOutterContainer={!quote}
                    carousel={showContentAsCarousel}
                  />
                </div>
              )
            })}
        </div>
        {blockCards.length > 3 ||
        (quote && blockCards.length > 1) ||
        (typeof window !== 'undefined' &&
          navigator.userAgent.match(
            /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i
          ))
          ? arrows
          : null}
      </div>
    ) : null

  let itemNumber
  if (showContentAsCarousel) {
    itemNumber = 3
  }
  if (quote) {
    itemNumber = 1
  }

  // mobile devices only show 1 item
  if (
    typeof window !== 'undefined' &&
    navigator.userAgent.match(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i)
  ) {
    itemNumber = 1
  }

  useEffect(() => {
    if (
      (blockCards && blockCards.length > 1 && showContentAsCarousel) ||
      (blockCards && blockCards.length > 1 && !displayInMobile && displayInMobile !== null)
    ) {
      const options = {
        swipeContainer: swipeContainer.current,
        itemContainer: itemContainer.current,
        prevButton: prevButton.current,
        nextButton: nextButton.current,
        wrapAround: true, // false will stop turning when reaching first or last item
        numberFocusedItems: itemNumber // how many items to show at once
      }
      carouselize(options)
    }
  }, [])

  // Theme change grid background color //
  let backgroundColor

  if (theme === 'Dark') {
    backgroundColor = 'bg-navy'
  }
  if (theme === 'Gray') {
    backgroundColor = 'bg-gray'
  }
  if (theme === 'Light Gray') {
    backgroundColor = 'bg-light-gray'
  }
  if (theme === 'White') {
    backgroundColor = 'bg-white'
  }
  if (theme === 'Primary Blue') {
    backgroundColor = 'bg-blue'
  }

  // create a background image for block card grid
  let bgImageComp
  if (backgroundImageGrid) {
    bgImageComp = (
      <GatsbyImage className="background" image={getImage(backgroundImageGrid.image)} alt="" />
    )
  }

  // Change the subtitle color in the grid layout
  const subtitleColor = theme === 'Dark' ? 'text-dark-theme' : 'text-light-theme'

  // change the location in the grid button
  const buttonLocation = isBlog ? 'button-left' : 'button-center'

  const gridButton =
    ctaButton !== null ? (
      <div
        className={
          blockCards
            ? `grid-button-div ${buttonLocation}`
            : `grid-button-div ${buttonLocation} no-margin`
        }
      >
        <ContentfulButtonV3 parentComponent={__typename} data={ctaButton[0]} grid blog={isBlog} />
      </div>
    ) : (
      ''
    )

  let headlineComp
  if (headline && headline.trim().length) {
    headlineComp = (
      <HeadlineBlockCard isGridHeadline text={headline} theme={theme} alignText={alignText} />
    )
  }
  /* eslint-disable react/no-danger */

  let subtitleComp
  if (subtitle && subtitle.trim().length) {
    subtitleComp = (
      <p className={`subtitle ${subtitleColor}`}>
        {subtitle
          .replace(/\\u00A0/g, '\u00A0')
          .split(/([[{].+?[\]}])/)
          .map((part, index) => {
            const lastIndex = subtitle.replace(/\\u00A0/g, '\u00A0').split(/([[{].+?[\]}])/).length
            /* eslint-disable react/no-array-index-key */
            if (part === '{dbr}') return <span key={`${part}-${index}`} className="desktop-break" />

            if (index === lastIndex - 1 && part.indexOf('<br') > -1) {
              const lastLineParts = part.split('<br>')
              return lastLineParts.map(lastLinePart => {
                return <span key={lastLinePart}>{lastLinePart}</span>
              })
            }
            if (index === lastIndex - 1 && part.indexOf('<br') === -1) {
              return <span key={part}>{part}</span>
            }
            return `${part} `
          })}
      </p>
    )
  }

  const cards =
    blockCards &&
    blockCards.map((card, index) => {
      return (
        <BlockCardV3
          data={card}
          key={JSON.stringify(card)}
          grid
          columns={gridClass(grid)}
          blog={isBlog}
          showcaseCircles={showcaseCircles}
          gridTheme={theme}
          cardNumber={index}
          quote={quote}
          imageGrid={backgroundImageGrid}
          gridLeadershipDesign={activeLeadershipDesign}
        />
      )
    })

  // carousel in mobile layout is just 3 stacked cards
  if (showContentAsCarousel && cards.length > 3) {
    while (cards.length > 3) {
      cards.pop()
    }
  }

  // set the class for show the carousel on mobile or dekstop
  let blockLayout
  if (!displayInMobile && displayInMobile !== null && showContentAsCarousel) {
    blockLayout = ''
  }
  if (!displayInMobile && displayInMobile !== null && !showContentAsCarousel) {
    blockLayout = 'no-mobile-carousel'
  }
  return (
    <div className={`${backgroundColor} ${backgroundImageGrid || quote ? 'no-pd' : ''}`}>
      <div
        className={
          headline && blockCards
            ? `headline-div-padding ${textAligner(alignText)}`
            : `headline-div-padding ${textAligner(alignText)} no-pd-bottom`
        }
      >
        {headlineComp}
        <div className={`subtitle-div-width ${textAligner(alignText)}`}>{subtitleComp}</div>
      </div>
      <div
        className={`${gridClass(grid)} ${showContentAsCarousel ? 'block-card-carousel' : ''} ${
          displayInMobile && showContentAsCarousel ? 'vertically-stacked-mobile' : ''
        } ${blockLayout} ${activeLeadershipDesign ? 'leadership-padding-top' : ''}`}
      >
        {bgImageComp}
        {cards}
      </div>

      <div className={`${quote ? 'grid-default quote-grid' : ''}`}>
        {quote ? bgImageComp : ''}
        {showContentAsCarousel || !displayInMobile ? carousel : null}
      </div>
      {gridButton}
    </div>
  )
}

BlockCardGrid.propTypes = {
  data: PropTypes.object.isRequired
}

export default BlockCardGrid
